import { usePortalAccess } from '../../components/PortalAccess';
import { useEffect, useState } from 'react'
import { getMonthlyStats, createMonthlyStats, updateMonthlyStats, getPlatform } from '../../utils/smmApi'
import { KPICard } from '../../components/smm/KPICard'
import { PeriodSelector } from '../../components/smm/PeriodSelector'
import { formatNumber, formatMonth } from '../../utils/smmFormat'
import styles from './SmmMax.module.css'

const PLATFORM_SLUG = 'max'
const PLATFORM_COLOR = '#FF6600'

const MONTH_PERIOD_OPTIONS = [
  { label: '3 мес.', value: 3 },
  { label: '6 мес.', value: 6 },
  { label: 'Год', value: 12 },
]



function emptyForm() {
  const now = new Date()
  const prevMonth = now.getMonth() === 0 ? 12 : now.getMonth()
  const prevYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear()
  return {
    year: prevYear, month: prevMonth,
    subscribers: '', total_views: '', total_reach: '', total_likes: '',
    total_reposts: '', total_comments: '', posts_count: '', note: '',
  }
}

export default function SmmMax() {
  const { permissions } = usePortalAccess();
  const canManage = permissions['smm.manage'] === true;
  const [platform, setPlatform] = useState(null)
  const [stats, setStats] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(emptyForm())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [period, setPeriod] = useState(6)

  useEffect(() => {
    Promise.all([getPlatform(PLATFORM_SLUG), getMonthlyStats(PLATFORM_SLUG)])
      .then(([p, s]) => { setPlatform(p); setStats(s) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  const latest = stats[stats.length - 1]

  const liveER = (() => {
    const reactions = parseInt(form.total_likes) || 0
    const comments = parseInt(form.total_comments) || 0
    const subs = parseInt(form.subscribers) || 0
    if (!subs) return null
    return ((reactions + comments) / subs * 100).toFixed(2)
  })()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const data = {
        platform_id: platform.id,
        year: form.year, month: form.month,
        subscribers: parseInt(form.subscribers) || null,
        posts_count: parseInt(form.posts_count) || null,
        total_views: parseInt(form.total_views) || null,
        total_reach: parseInt(form.total_reach) || null,
        total_likes: parseInt(form.total_likes) || null,
        total_reposts: parseInt(form.total_reposts) || null,
        total_comments: parseInt(form.total_comments) || null,
        note: form.note || null,
      }
      let saved
      if (editId) {
        saved = await updateMonthlyStats(editId, data)
        setStats(prev => prev.map(s => s.id === editId ? saved : s))
      } else {
        saved = await createMonthlyStats(data)
        setStats(prev => [...prev, saved].sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month)))
      }
      setShowForm(false)
      setEditId(null)
      setForm(emptyForm())
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка при сохранении')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (stat) => {
    setEditId(stat.id)
    setForm({
      year: stat.year, month: stat.month,
      subscribers: stat.subscribers?.toString() ?? '',
      total_views: stat.total_views?.toString() ?? '',
      total_reach: stat.total_reach?.toString() ?? '',
      total_likes: stat.total_likes?.toString() ?? '',
      total_reposts: stat.total_reposts?.toString() ?? '',
      total_comments: stat.total_comments?.toString() ?? '',
      posts_count: stat.posts_count?.toString() ?? '',
      note: stat.note ?? '',
    })
    setShowForm(true)
    setTimeout(() => document.getElementById('max-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  const visibleStats = stats.slice(-period).reverse()

  if (loading) return <div className={styles.loading}><span className="spinner" /></div>

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.platformDot} style={{ background: PLATFORM_COLOR }} />
          <div>
            <h1 className={styles.title}>Макс</h1>
            <p className={styles.subtitle}>Ручной ввод · Минимальная статистика</p>
          </div>
        </div>
        <div className={styles.headerRight}>
          <PeriodSelector options={MONTH_PERIOD_OPTIONS} value={period} onChange={v => setPeriod(Number(v))} />
          {canManage && (
            <button className="btn btn-primary" onClick={() => {
              setEditId(null); setForm(emptyForm()); setShowForm(true)
              setTimeout(() => document.getElementById('max-form')?.scrollIntoView({ behavior: 'smooth' }), 100)
            }}>
              + Внести данные за месяц
            </button>
          )}
        </div>
      </div>

      {latest && (
        <div className={styles.kpiGrid}>
          <KPICard label="Подписчики" value={formatNumber(latest.subscribers)} tooltip="Подписчики на конец месяца" source="Вручную" />
          <KPICard label="Прирост" value={latest.subscriber_growth !== null ? (latest.subscriber_growth >= 0 ? `+${formatNumber(latest.subscriber_growth)}` : `${formatNumber(latest.subscriber_growth)}`) : '—'} tooltip="Текущие − предыдущие" source="Расчёт" />
          <KPICard label="Просмотры" value={formatNumber(latest.total_views)} tooltip="Суммарные просмотры постов за месяц" source="Вручную" />
          <KPICard label="Реакции" value={formatNumber(latest.total_likes)} tooltip="Суммарные реакции за месяц" source="Вручную" />
          <KPICard label="ER месяца" value={latest.er !== null ? `${latest.er}%` : '—'} tooltip="(реакции+комм) / подписчики × 100" source="Расчёт"
            color={latest.er !== null ? (latest.er >= 6 ? 'var(--er-high)' : latest.er >= 4 ? 'var(--er-mid)' : 'var(--er-low)') : undefined} />
          <KPICard label="Ср. просмотров" value={latest.avg_post_reach !== null ? formatNumber(Math.round(latest.avg_post_reach)) : '—'} tooltip="Просмотры / постов за месяц" source="Расчёт" />
        </div>
      )}

      {stats.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>История по месяцам</h2>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Месяц</th><th>Подп.</th><th>Прирост</th><th>Просмотры</th><th>Охват</th>
                  <th>Реакции</th><th>Репосты</th><th>Комм.</th><th>ER</th><th>Постов</th><th>Заметка</th>
                  {canManage && <th></th>}
                </tr>
              </thead>
              <tbody>
                {visibleStats.map(s => (
                  <tr key={s.id}>
                    <td className={styles.monthCell}>{formatMonth(s.year, s.month)}</td>
                    <td>{formatNumber(s.subscribers)}</td>
                    <td>
                      {s.subscriber_growth !== null ? (
                        <span style={{ color: s.subscriber_growth >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                          {s.subscriber_growth >= 0 ? '+' : ''}{formatNumber(s.subscriber_growth)}
                        </span>
                      ) : '—'}
                    </td>
                    <td>{formatNumber(s.total_views)}</td>
                    <td>{formatNumber(s.total_reach)}</td>
                    <td>{formatNumber(s.total_likes)}</td>
                    <td>{formatNumber(s.total_reposts)}</td>
                    <td>{formatNumber(s.total_comments)}</td>
                    <td className={s.er !== null ? (s.er >= 6 ? 'er-high' : s.er >= 4 ? 'er-mid' : 'er-low') : ''}>
                      {s.er !== null ? `${s.er}%` : '—'}
                    </td>
                    <td>{s.posts_count ?? '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.note || '—'}</td>
                    {canManage && <td><button className="btn btn-ghost btn-sm" onClick={() => handleEdit(s)}>Изменить</button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {canManage && showForm && (
        <section id="max-form" className={styles.formSection}>
          <div className={styles.formHeader}>
            <h2 className={styles.sectionTitle}>{editId ? 'Редактировать данные' : 'Внести данные за месяц'}</h2>
            <button className="btn btn-ghost btn-sm" onClick={() => { setShowForm(false); setEditId(null) }}>✕ Закрыть</button>
          </div>

          <div className={styles.hintBlock} style={{ marginBottom: 20 }}>
            <h4>📱 Где найти данные в приложении Макс</h4>
            <ul className={styles.hintList}>
              <li><strong>Подписчики:</strong> Профиль → счётчик подписчиков</li>
              <li><strong>Просмотры:</strong> Статистика → Просмотры → суммарно за месяц</li>
              <li><strong>Реакции:</strong> Статистика → Реакции → суммарно</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            <div className={styles.formRow}>
              <div className="form-group">
                <label className="form-label">Год</label>
                <select className="form-input" value={form.year} onChange={e => setForm(f => ({ ...f, year: parseInt(e.target.value) }))}>
                  {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Месяц</label>
                <select className="form-input" value={form.month} onChange={e => setForm(f => ({ ...f, month: parseInt(e.target.value) }))}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{formatMonth(form.year, m)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <h3 className={styles.fieldGroupTitle}>Обязательные поля</h3>
              <div className={styles.formGrid}>
                <Field label="Подписчики *" value={form.subscribers} onChange={v => setForm(f => ({ ...f, subscribers: v }))} />
                <Field label="Постов за месяц *" value={form.posts_count} onChange={v => setForm(f => ({ ...f, posts_count: v }))} />
                <Field label="Просмотры *" value={form.total_views} onChange={v => setForm(f => ({ ...f, total_views: v }))} />
                <Field label="Реакции *" value={form.total_likes} onChange={v => setForm(f => ({ ...f, total_likes: v }))} />
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <h3 className={styles.fieldGroupTitle}>Необязательные поля</h3>
              <div className={styles.formGrid}>
                <Field label="Охват (уник.)" value={form.total_reach} onChange={v => setForm(f => ({ ...f, total_reach: v }))} />
                <Field label="Репосты / пересылки" value={form.total_reposts} onChange={v => setForm(f => ({ ...f, total_reposts: v }))} />
                <Field label="Комментарии" value={form.total_comments} onChange={v => setForm(f => ({ ...f, total_comments: v }))} />
              </div>
            </div>

            {liveER && (
              <div className={styles.liveER}>
                <span className={styles.liveERLabel}>Расчёт в реальном времени:</span>
                <span>ER = <strong style={{ color: 'var(--er-mid)' }}>{liveER}%</strong></span>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Заметка к месяцу</label>
              <textarea className="form-input" rows={3} placeholder="Контекст для клиента..."
                value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                style={{ resize: 'vertical' }} />
            </div>

            {error && <div className={styles.formError}>{error}</div>}

            <div className={styles.formActions}>
              <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null) }}>Отмена</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner" /> : editId ? 'Сохранить изменения' : 'Сохранить'}
              </button>
            </div>
          </form>
        </section>
      )}
    </div>
  )
}

function Field({ label, hint, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input type="number" className="form-input" value={value} onChange={e => onChange(e.target.value)} min={0} placeholder="0" />
      {hint && <span className="form-hint">{hint}</span>}
    </div>
  )
}
